<template>
  <woot-modal
    :show="show"
    :on-close="onClose"
    size="large"
    :show-close-button="false"
  >
    <div class="hub-modal">
      <div class="fb-header">
        <div class="hd-left">
          <button
            class="icon-btn"
            type="button"
            :aria-label="$t('HUB.FB_POSTS.CLOSE')"
            @click="onClose"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <h2>
          {{
            isEditing ? $t('HUB.FB_POSTS.EDIT_TITLE') : $t('HUB.FB_POSTS.TITLE')
          }}
        </h2>
        <div class="hd-right" />
      </div>

      <!-- List view -->
      <template v-if="!isEditing">
        <p class="posts-description">{{ $t('HUB.FB_POSTS.DESCRIPTION') }}</p>

        <div class="posts-body">
          <div v-if="isLoading" class="posts-state">
            <spinner />
          </div>
          <div v-else-if="!posts.length" class="posts-state">
            {{ $t('HUB.FB_POSTS.EMPTY') }}
          </div>
          <hub-posts-table
            v-else
            :columns="columns"
            :rows="posts"
            :labels="tableLabels"
            @copy="copyContent"
            @edit="onEdit"
            @delete="onDelete"
          />
        </div>

        <div class="modal-footer">
          <hub-pagination
            class="modal-footer__pager"
            :current-page="currentPage"
            :total-pages="totalPages"
            @page-change="onPageChange"
          />
          <button
            class="btn btn-ghost modal-footer__cancel"
            type="button"
            @click="onClose"
          >
            {{ $t('HUB.FB_POSTS.CLOSE') }}
          </button>
        </div>
      </template>

      <!-- Edit view: 2-step (compose / preview), reuses the create-post preview -->
      <template v-else>
        <div class="edit-body">
          <div v-show="editStep === 1" class="edit-form">
            <div class="edit-form__inner">
              <div class="edit-info">
                <div class="edit-info__row">
                  <span class="edit-info__label">
                    {{ $t('HUB.FB_POSTS.COLUMNS.POSTER') }}
                  </span>
                  <span class="edit-info__value">
                    {{ posterName(editingPost) }}
                  </span>
                </div>
                <div class="edit-info__row">
                  <span class="edit-info__label">
                    {{ $t('HUB.FB_POSTS.COLUMNS.STATUS') }}
                  </span>
                  <span
                    class="status-tag"
                    :class="`status-${statusInfo(editingPost).variant}`"
                  >
                    {{ statusInfo(editingPost).label }}
                  </span>
                </div>
                <div class="edit-info__row">
                  <span class="edit-info__label">
                    {{ $t('HUB.FB_POSTS.COLUMNS.CREATED_AT') }}
                  </span>
                  <span class="edit-info__value">
                    {{ formatDate(editingPost.created_at) }}
                  </span>
                </div>
                <div class="edit-info__row">
                  <span class="edit-info__label">
                    {{ $t('HUB.FB_POSTS.COLUMNS.PUBLISHED_AT') }}
                  </span>
                  <span class="edit-info__value">
                    {{ formatDate(editingPost.published_at) }}
                  </span>
                </div>
              </div>

              <div class="edit-field">
                <label class="edit-field__label" for="hub-edit-content">
                  {{ $t('HUB.FB_POSTS.COLUMNS.CONTENT') }}
                </label>
                <textarea
                  id="hub-edit-content"
                  v-model="editContent"
                  class="edit-field__input"
                  rows="7"
                  :maxlength="maxMessageLength"
                  :placeholder="$t('HUB.FB_POSTS.EDIT_CONTENT_PLACEHOLDER')"
                />
                <div class="edit-field__count">
                  {{ editContent.length }} / {{ maxMessageLength }}
                </div>
              </div>
            </div>
          </div>

          <hub-facebook-post-preview
            v-show="editStep === 2"
            :page-name="pageName"
            :page-picture="pagePicture"
            :message="editContent"
          />
        </div>

        <div class="modal-footer modal-footer--edit">
          <button class="btn btn-ghost" type="button" @click="onEditBack">
            {{ $t('HUB.FB_POSTS.BACK') }}
          </button>
          <button
            v-if="editStep === 1"
            class="btn btn-primary"
            type="button"
            :disabled="!editContent.trim()"
            @click="editStep = 2"
          >
            {{ $t('HUB.FB_POSTS.NEXT') }}
          </button>
          <button
            v-else
            class="btn btn-primary"
            type="button"
            :disabled="isSubmitting || !editContent.trim()"
            @click="submitEdit"
          >
            {{ $t('HUB.FB_POSTS.SUBMIT') }}
          </button>
        </div>
      </template>

      <!-- Delete confirmation -->
      <div
        v-if="deleteTarget"
        class="confirm-overlay"
        @click.self="cancelDelete"
      >
        <div class="confirm-card" role="dialog" aria-modal="true">
          <div class="confirm-head">
            <div class="confirm-title">
              <svg
                class="confirm-title__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
              <span>{{ $t('HUB.FB_POSTS.DELETE_CONFIRM_TITLE') }}</span>
            </div>
            <button
              class="icon-btn"
              type="button"
              :aria-label="$t('HUB.FB_POSTS.CANCEL')"
              @click="cancelDelete"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <p class="confirm-message">
            {{ $t('HUB.FB_POSTS.DELETE_CONFIRM_MESSAGE') }}
          </p>
          <div class="confirm-actions">
            <button class="btn btn-ghost" type="button" @click="cancelDelete">
              {{ $t('HUB.FB_POSTS.CANCEL') }}
            </button>
            <button
              class="btn btn-danger"
              type="button"
              :disabled="isDeleting"
              @click="confirmDelete"
            >
              {{ $t('HUB.FB_POSTS.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner.vue';
import HubPostsTable from '../shared/HubPostsTable.vue';
import HubPagination from '../shared/HubPagination.vue';
import HubFacebookPostPreview from './HubFacebookPostPreview.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import hubClient from 'dashboard/api/channel/hubClient';

const PER_PAGE = 25;
const MAX_MESSAGE_LENGTH = 63206;

export default {
  components: {
    Spinner,
    HubPostsTable,
    HubPagination,
    HubFacebookPostPreview,
  },
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    inbox: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      posts: [],
      isLoading: false,
      currentPage: 1,
      totalCount: 0,
      perPage: PER_PAGE,
      editingPost: null,
      editStep: 1,
      editContent: '',
      isSubmitting: false,
      maxMessageLength: MAX_MESSAGE_LENGTH,
      deleteTarget: null,
      isDeleting: false,
    };
  },
  computed: {
    isEditing() {
      return this.editingPost !== null;
    },
    pageAttributes() {
      return this.inbox?.additional_attributes || {};
    },
    pageName() {
      return this.pageAttributes.page_name || '';
    },
    pagePicture() {
      return this.pageAttributes.page_picture || '';
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.perPage));
    },
    tableLabels() {
      return {
        copy: this.$t('HUB.FB_POSTS.COPY'),
        edit: this.$t('HUB.FB_POSTS.EDIT'),
        delete: this.$t('HUB.FB_POSTS.DELETE'),
      };
    },
    columns() {
      const t = key => this.$t(`HUB.FB_POSTS.COLUMNS.${key}`);
      const metricsTooltip = this.$t('HUB.FB_POSTS.METRICS_TOOLTIP');
      return [
        {
          key: 'poster',
          type: 'user',
          label: t('POSTER'),
          value: this.posterName,
        },
        {
          key: 'status',
          type: 'status',
          label: t('STATUS'),
          width: '140px',
          value: this.statusInfo,
        },
        { key: 'content', type: 'content', label: t('CONTENT') },
        {
          key: 'comments',
          type: 'metric',
          label: t('COMMENTS'),
          width: '84px',
          tooltip: metricsTooltip,
        },
        {
          key: 'likes',
          type: 'metric',
          label: t('LIKES'),
          width: '84px',
          tooltip: metricsTooltip,
        },
        {
          key: 'created_at',
          type: 'date',
          label: t('CREATED_AT'),
          width: '150px',
        },
        {
          key: 'updated_at',
          type: 'date',
          label: t('UPDATED_AT'),
          width: '150px',
        },
        {
          key: 'published_at',
          type: 'date',
          label: t('PUBLISHED_AT'),
          width: '150px',
        },
        {
          key: 'actions',
          type: 'actions',
          label: t('ACTIONS'),
          width: '110px',
        },
      ];
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.currentPage = 1;
        this.resetEdit();
        this.deleteTarget = null;
        this.fetchPosts();
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('update:show', false);
    },
    posterName(post) {
      return post.user?.name || this.$t('HUB.FB_POSTS.UNKNOWN_USER');
    },
    // `waiting` isn't stored: a completed post whose publish time is still in
    // the future is shown as pending, otherwise as completed (self-updating).
    statusInfo(post) {
      if (post.status === 'failed') {
        return {
          label: this.$t('HUB.FB_POSTS.STATUS.FAILED'),
          variant: 'failed',
        };
      }
      const pending =
        post.published_at && new Date(post.published_at) > new Date();
      if (pending) {
        return {
          label: this.$t('HUB.FB_POSTS.STATUS.PENDING'),
          variant: 'pending',
        };
      }
      return {
        label: this.$t('HUB.FB_POSTS.STATUS.COMPLETED'),
        variant: 'completed',
      };
    },
    async copyContent(content) {
      try {
        await copyTextToClipboard(content || '');
        this.showAlert(this.$t('HUB.FB_POSTS.COPIED'));
      } catch (error) {
        this.showAlert(this.$t('HUB.FB_POSTS.COPY_FAILED'));
      }
    },
    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString();
    },
    onEdit(post) {
      this.editingPost = post;
      this.editContent = post.content || '';
      this.editStep = 1;
    },
    onEditBack() {
      // Step 2 goes back to the compose step; step 1 returns to the list.
      if (this.editStep === 2) {
        this.editStep = 1;
        return;
      }
      this.resetEdit();
    },
    resetEdit() {
      this.editingPost = null;
      this.editStep = 1;
      this.editContent = '';
      this.isSubmitting = false;
    },
    async submitEdit() {
      const content = this.editContent.trim();
      if (this.isSubmitting || !content || !this.editingPost) return;

      this.isSubmitting = true;
      try {
        await hubClient.updatePost(this.editingPost.id, {
          inbox_id: this.inbox.id,
          message: this.editContent,
        });
        // The hub edit runs async in a job; reflect the new body optimistically.
        this.editingPost.content = this.editContent;
        this.showAlert(this.$t('HUB.FB_POSTS.EDIT_SUCCESS'));
        this.resetEdit();
      } catch (error) {
        this.showAlert(this.$t('HUB.FB_POSTS.EDIT_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
    onDelete(post) {
      this.deleteTarget = post;
    },
    cancelDelete() {
      if (this.isDeleting) return;
      this.deleteTarget = null;
    },
    async confirmDelete() {
      const post = this.deleteTarget;
      if (this.isDeleting || !post) return;

      this.isDeleting = true;
      try {
        await hubClient.deletePost(post.id, { inbox_id: this.inbox.id });
        // The hub deletion runs async in a job; drop the row optimistically.
        this.posts = this.posts.filter(item => item.id !== post.id);
        this.totalCount = Math.max(0, this.totalCount - 1);
        this.showAlert(this.$t('HUB.FB_POSTS.DELETE_SUCCESS'));
        this.deleteTarget = null;
      } catch (error) {
        this.showAlert(this.$t('HUB.FB_POSTS.DELETE_ERROR'));
      } finally {
        this.isDeleting = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.fetchPosts();
    },
    async fetchPosts() {
      if (!this.inbox?.id) return;
      this.isLoading = true;
      try {
        const { data } = await hubClient.getFacebookPosts(
          this.inbox.id,
          this.currentPage
        );
        this.posts = data.payload || [];
        this.totalCount = data.meta?.count || 0;
      } catch (error) {
        this.showAlert(this.$t('HUB.FB_POSTS.LOAD_ERROR'));
        this.posts = [];
        this.totalCount = 0;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Light Facebook surface, matching HubFacebookPageModal. */
.hub-modal {
  --cw-green: #23a06d;
  --cw-green-dark: #1f845a;
  --cw-green-tint: #eaf6f0;
  --error-red: #b91c1c;
  --fb-surface: #f0f2f5;
  --ink: #262626;
  --ink-soft: #374151;
  --muted: #6b7280;
  --line: #d1d5db;
  --line-soft: #efefef;
  --surface: #ffffff;
  --radius-sm: 5px;

  position: relative;
  width: 100%;
  height: 80vh;
  max-height: calc(100vh - 4rem);
  background: var(--surface);
  color: var(--ink-soft);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.hub-modal *,
.hub-modal *::before,
.hub-modal *::after {
  box-sizing: border-box;
}

/* Header */
.fb-header {
  direction: ltr;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 46px;
  padding: 0 8px 0 12px;
  border-bottom: 1px solid var(--line);
  flex: none;
}
.fb-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  text-align: center;
  flex: 1;
}
.hd-left,
.hd-right {
  display: flex;
  align-items: center;
  min-width: 40px;
}
.hd-right {
  justify-content: flex-end;
}
.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 50%;
  color: var(--ink);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s;
}
.icon-btn:hover {
  background: var(--line-soft);
}

/* Body */
.posts-description {
  flex: none;
  margin: 0;
  padding: 10px 20px 12px;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
  border-bottom: 1px solid var(--line-soft);
}
.posts-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 20px;
  overflow: hidden;
}
.posts-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: var(--muted);
  font-size: 14px;
}
.modal-footer {
  flex: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid var(--line);
  background: #fff;
}
.modal-footer__pager {
  grid-column: 2;
}
.modal-footer__cancel {
  grid-column: 3;
  justify-self: end;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  border-radius: var(--radius-sm);
  padding: 0 22px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.btn-ghost {
  background: #fff;
  color: var(--ink-soft);
  border: 1px solid var(--line);
}
.btn-ghost:hover {
  background: var(--line-soft);
}
.btn-primary {
  background: var(--cw-green);
  color: #fff;
  border: 1px solid var(--cw-green);
}
.btn-primary:not(:disabled):hover {
  background-color: var(--cw-green-dark);
  border-color: var(--cw-green-dark);
}
.btn-primary:disabled {
  background-color: #e5e7eb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}
.modal-footer--edit {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Edit view */
.edit-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.edit-form {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px;
}
.edit-form__inner {
  max-width: 520px;
  margin: 0 auto;
}
.edit-info {
  border: 1px solid var(--line-soft);
  border-radius: 8px;
  padding: 4px 14px;
  margin-bottom: 20px;
}
.edit-info__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
}
.edit-info__row + .edit-info__row {
  border-top: 1px solid var(--line-soft);
}
.edit-info__label {
  color: var(--muted);
  font-weight: 600;
}
.edit-info__value {
  color: var(--ink);
  text-align: right;
  word-break: break-word;
}
.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  outline: 1px solid;
  outline-offset: -1px;
}
.status-completed {
  background: rgba(31, 132, 90, 0.08);
  outline-color: #1f845a;
  color: #1f845a;
}
.status-failed {
  background: rgba(239, 68, 68, 0.08);
  outline-color: #ef4444;
  color: #ef4444;
}
.status-pending {
  background: rgba(217, 119, 6, 0.08);
  outline-color: #d97706;
  color: #d97706;
}
.edit-field__label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}
.edit-field__input {
  width: 100%;
  resize: vertical;
  min-height: 140px;
  padding: 12px 14px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.edit-field__input:focus {
  outline: none;
  border-color: var(--cw-green);
  box-shadow: 0 0 0 3px var(--cw-green-tint);
}
.edit-field__count {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
  text-align: right;
}

/* Delete confirmation */
.btn-danger {
  background: #ef4444;
  color: #fff;
  border: 1px solid #ef4444;
}
.btn-danger:not(:disabled):hover {
  background-color: #dc2626;
  border-color: #dc2626;
}
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.confirm-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
}
.confirm-card {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.confirm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.confirm-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  line-height: 20px;
  color: #323431;
}
.confirm-title__icon {
  width: 22px;
  height: 22px;
  color: var(--muted);
  flex: none;
}
.confirm-message {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: #4b5563;
}
.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
